import { Logger } from '../../feature/logger/logger';
import { AuthResponse, AuthStorageKeys, PkceParams, UserInfo } from './auth.model';
import { TokenService } from './token.service';

export class AuthStorageService implements TokenService {
  shouldRefresh(): boolean {
    return true;
  }

  static saveAuthResponse(authResponse: AuthResponse): { accessToken: string; refreshToken: string } {
    Logger.debug('saveAuthResponse', 'Saving auth response');
    const accessToken = authResponse.access_token;
    const refreshToken = authResponse.refresh_token;
    localStorage.setItem(AuthStorageKeys.ACCESS_TOKEN, accessToken);
    localStorage.setItem(AuthStorageKeys.ID_TOKEN, authResponse.id_token);
    localStorage.setItem(AuthStorageKeys.REFRESH_TOKEN, refreshToken);

    return { accessToken, refreshToken };
  }

  static saveWarpAuthResponse(id_token: string) {
    Logger.debug('saveWarpAuthResponse', 'Saving WARP auth response');
    localStorage.setItem(AuthStorageKeys.ID_TOKEN, id_token);

    return { id_token };
  }

  static savePkceFlowData({ codeVerifier, state, csrf }: PkceParams) {
    Logger.debug('savePkceFlowData', 'Saving PKCE flow data');
    localStorage.setItem(AuthStorageKeys.CODE_VERIFIER, codeVerifier);
    localStorage.setItem(AuthStorageKeys.STATE, state);
    localStorage.setItem(AuthStorageKeys.CSRF, csrf);
  }

  static getAccessToken(): string | null {
    return localStorage.getItem(AuthStorageKeys.ACCESS_TOKEN);
  }

  static getRefreshToken(): string | null {
    return localStorage.getItem(AuthStorageKeys.REFRESH_TOKEN);
  }

  static getIdToken(): string | null {
    return localStorage.getItem(AuthStorageKeys.ID_TOKEN);
  }

  static saveUserInfo({ stihl_id: _ignoreId, anonymousId: _ignoreAnonymousId, ...userInfo }: UserInfo) {
    localStorage.setItem(AuthStorageKeys.USER_INFO, JSON.stringify(userInfo));
  }

  static clearUserInfo() {
    Logger.debug('clearUserInfo', 'Clearing user info');
    localStorage.removeItem(AuthStorageKeys.USER_INFO);
  }

  static getUserInfo(): UserInfo | null {
    try {
      const userInfo = localStorage.getItem(AuthStorageKeys.USER_INFO);
      return userInfo ? JSON.parse(userInfo) : null;
    } catch (error) {
      return null;
    }
  }

  static getPkceFlowData(): {
    codeVerifier: string | null;
    state: string | null;
    csrf: string | null;
  } {
    const codeVerifier = localStorage.getItem(AuthStorageKeys.CODE_VERIFIER);
    const state = localStorage.getItem(AuthStorageKeys.STATE);
    const csrf = localStorage.getItem(AuthStorageKeys.CSRF);

    return { codeVerifier, state, csrf };
  }

  static hasPkceFlowData(): boolean {
    const { codeVerifier, state, csrf } = this.getPkceFlowData();
    return !!codeVerifier && !!state && !!csrf;
  }

  static hasAuthData(): boolean {
    return !!this.getIdToken() || this.hasPkceFlowData();
  }

  static clearSession() {
    Logger.debug('clearSession', 'Clearing session');
    Object.keys(localStorage)
      .filter((key) => key.startsWith('PHOENIX_AUTH_'))
      .forEach((key) => localStorage.removeItem(key));
  }

  static clearPkceFlowData() {
    Logger.debug('clearPkceFlowData', 'Clearing PKCE flow data');
    localStorage.removeItem(AuthStorageKeys.CODE_VERIFIER);
    localStorage.removeItem(AuthStorageKeys.STATE);
    localStorage.removeItem(AuthStorageKeys.CSRF);
  }
}
